import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import DropdownWithSearchOils from "../DropdownWithSearchOils";
import ConfirmationDialog from "../ConfirmationDialog";
import ExportModal from "../ExportModal";
import MostreDashboard from "../MostreDashboard";
import reminderSchedulerService from "../../services/reminderSchedulerService";
import followUpReminderService from '../../services/followUpReminderService';
import { oilsDatabase } from "../../utils/oilsDatabase";
import { Search, Filter, X, ChevronDown, Download, Settings, BarChart3, Trash2 } from "lucide-react";
import {
  deleteCampaignAppointment,
  getAppointmentDocumentId,
} from "../../services/appointmentService";
import {
  getAppointmentSelectionLabel,
  toggleAllAppointmentSelection,
  toggleAppointmentSelection,
} from "../../utils/appointmentSelection";
import "./MostreTable.scss";

const getAppointmentRowKey = (appointment) => {
  const campaignId = String(appointment?.campaignId || "").trim();
  const appointmentId = getAppointmentDocumentId(appointment);
  return campaignId && appointmentId ? `${campaignId}_${appointmentId}` : "";
};

const MostreTable = forwardRef((props, ref) => {
  const [allAppointments, setAllAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [userNotes, setUserNotes] = useState({});
  const [userMultipleOils, setUserMultipleOils] = useState({});
  const [loading, setLoading] = useState(true);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [selectedAppointmentKeys, setSelectedAppointmentKeys] = useState(new Set());
  const [deleteDialogAppointments, setDeleteDialogAppointments] = useState([]);
  const [isDeletingAppointments, setIsDeletingAppointments] = useState(false);
  const selectAllCheckboxRef = React.useRef(null);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    campanie: "",
    oras: "",
    incercat: "",
    cont: "",
    provocari: "",
    mostre: "",
    inscris: ""
  });

  // Column selection and scrolling states
  const [selectedColumns, setSelectedColumns] = useState(new Set());
  const [showScrollIndicators, setShowScrollIndicators] = useState({ left: false, right: false });

  // Configuration states
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const defaultColumns = {
      campaign: true,
      name: true,
      lastname: true,
      phone: true,
      city: true,
      triedOils: true,
      doterraAccount: true,
      challenges: true,
      samples: true,
      enrolled: true
    };
    
    return defaultColumns;
  });

  const [tableSettings, setTableSettings] = useState(() => {
    const saved = localStorage.getItem('mostre-table-settings');
    return saved ? { ...JSON.parse(saved), autoSave: true } : {
      rowsPerPage: 25,
      autoSave: true,
      compactView: false
    };
  });

  // Get unique values for filters
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const fetchAllCampaigns = async () => {
    try {
      const result = await axios.get(
        `${process.env.REACT_APP_API_BASE}/campaign/get`
      );
      return result.data;
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      return [];
    }
  };

  const fetchAppointmentsForCampaign = async (campaignId, campaignData) => {
    try {
      const result = await axios.get(
        `${process.env.REACT_APP_API_BASE}/appointment-campaign/get?id=${campaignId}`
      );
      return result.data.map(appointment => ({
        ...appointment,
        campaignId: campaignId,
        campaignName: campaignData.title || campaignId // Use campaign title instead of ID
      }));
    } catch (error) {
      console.error(`Error fetching appointments for campaign ${campaignId}:`, error);
      return [];
    }
  };

  const fetchAllAppointments = async () => {
    setLoading(true);
    try {
      // First fetch all campaigns
      const campaigns = await fetchAllCampaigns();
      
      // Then fetch appointments for each campaign
      const allAppointmentsPromises = campaigns.map(async (campaign) => {
        const appointments = await fetchAppointmentsForCampaign(campaign.id, campaign);
        return appointments;
      });

      const allAppointmentsArrays = await Promise.all(allAppointmentsPromises);
      const flattenedAppointments = allAppointmentsArrays.flat();
      
      setAllAppointments(flattenedAppointments);
      setFilteredAppointments(flattenedAppointments);

      // Extract unique values for filters
      const uniqueCampaigns = [...new Set(flattenedAppointments.map(apt => apt.campaignName))].sort();
      const uniqueCities = [...new Set(flattenedAppointments.map(apt => apt.city))].filter(Boolean).sort();
      
      setCampaignOptions(uniqueCampaigns);
      setCityOptions(uniqueCities);

      // Initialize user notes and multiple oils
      const initialNotes = {};
      const initialMultipleOils = {};

      flattenedAppointments.forEach((appointment) => {
        const appointmentKey = `${appointment.campaignId}_${appointment.id}`;
        const hasExistingData = appointment.provocari || appointment.mostre || appointment.multipleOils;

        if (hasExistingData) {
          initialNotes[appointmentKey] = {
            provocari: appointment.provocari || "",
            mostre: appointment.mostre || "",
            inscris: appointment.inscris || false,
          };

          if (appointment.multipleOils) {
            const oilsArray = appointment.multipleOils.split(", ").filter(oil => oil.trim() !== "");
            initialMultipleOils[appointmentKey] = oilsArray;
          }
        } else {
          initialNotes[appointmentKey] = {
            provocari: "",
            mostre: "",
            inscris: false,
          };
        }
      });

      setUserNotes(initialNotes);
      setUserMultipleOils(initialMultipleOils);

      // Schedule 2-week follow-up reminders for appointments that don't have regular reminders
      await scheduleInitialFollowUpReminders(flattenedAppointments);
    } catch (error) {
      console.error("Error fetching all appointments:", error);
      toast.error("Eroare la încărcarea datelor");
    } finally {
      setLoading(false);
    }
  };

  // Schedule follow-up reminders for appointments that need them
  const scheduleInitialFollowUpReminders = async (appointments) => {
    try {
      for (const appointment of appointments) {
        // Only schedule follow-up if appointment doesn't have regular reminder or follow-up already
        if (!appointment.reminder && !appointment.followUpReminder && 
            appointment.name && appointment.lastname && appointment.phone) {
          try {
            await followUpReminderService.scheduleFollowUpReminder(
              appointment.campaignId,
              appointment.id,
              appointment.name,
              appointment.lastname,
              appointment.phone
            );
            console.log(`Scheduled follow-up reminder for ${appointment.name} ${appointment.lastname}`);
          } catch (error) {
            console.error(`Error scheduling follow-up for ${appointment.name} ${appointment.lastname}:`, error);
            // Continue with other appointments even if one fails
          }
        }
      }
    } catch (error) {
      console.error('Error in scheduleInitialFollowUpReminders:', error);
    }
  };

  useEffect(() => {
    fetchAllAppointments();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Save configuration to localStorage
  useEffect(() => {
    localStorage.setItem('mostre-visible-columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    localStorage.setItem('mostre-table-settings', JSON.stringify(tableSettings));
  }, [tableSettings]);

  // Search functionality
  useEffect(() => {
    let filtered = allAppointments;

    // Apply search query
    if (searchQuery) {
      filtered = filtered.filter(appointment => 
        appointment.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        appointment.lastname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        appointment.phone?.includes(searchQuery)
      );
    }

    // Apply filters
    if (filters.campanie) {
      filtered = filtered.filter(appointment => appointment.campaignName === filters.campanie);
    }
    if (filters.oras) {
      filtered = filtered.filter(appointment => appointment.city === filters.oras);
    }
    if (filters.incercat) {
      filtered = filtered.filter(appointment => appointment.hasTriedEssentialOils === filters.incercat);
    }
    if (filters.cont) {
      filtered = filtered.filter(appointment => appointment.hasDoTerraAccount === filters.cont);
    }
    if (filters.provocari) {
      filtered = filtered.filter(appointment => {
        const key = `${appointment.campaignId}_${appointment.id}`;
        const provocari = userNotes[key]?.provocari || "";
        return provocari.toLowerCase().includes(filters.provocari.toLowerCase());
      });
    }
    if (filters.mostre) {
      filtered = filtered.filter(appointment => {
        const key = `${appointment.campaignId}_${appointment.id}`;
        const mostre = userNotes[key]?.mostre || "";
        return mostre.toLowerCase().includes(filters.mostre.toLowerCase());
      });
    }
    if (filters.inscris) {
      filtered = filtered.filter(appointment => {
        const key = `${appointment.campaignId}_${appointment.id}`;
        const inscris = userNotes[key]?.inscris || false;
        if (filters.inscris === "Da") {
          return inscris === true;
        } else if (filters.inscris === "Nu") {
          return inscris === false;
        }
        return true;
      });
    }

    setFilteredAppointments(filtered);
  }, [searchQuery, filters, allAppointments, userNotes]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      campanie: "",
      oras: "",
      incercat: "",
      cont: "",
      provocari: "",
      mostre: "",
      inscris: ""
    });
    setSearchQuery("");
  };

  // Column selection handlers
  const handleColumnClick = (columnName, event) => {
    if (event.ctrlKey || event.metaKey) {
      // Multi-select with Ctrl/Cmd
      setSelectedColumns(prev => {
        const newSet = new Set(prev);
        if (newSet.has(columnName)) {
          newSet.delete(columnName);
        } else {
          newSet.add(columnName);
        }
        return newSet;
      });
    } else {
      // Single select
      setSelectedColumns(new Set([columnName]));
    }
  };

  // Scroll handlers
  const handleScroll = (event) => {
    const container = event.target;
    const scrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;
    
    setShowScrollIndicators({
      left: scrollLeft > 0,
      right: scrollLeft < maxScroll
    });
  };

  const scrollTable = (direction) => {
    const container = document.querySelector('.table-scroll-container');
    if (container) {
      const scrollAmount = 300;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Clear column selection when clicking outside
  const clearColumnSelection = () => {
    setSelectedColumns(new Set());
  };

  // Initialize scroll indicators
  useEffect(() => {
    const checkScrollIndicators = () => {
      const container = document.querySelector('.table-scroll-container');
      if (container) {
        const scrollLeft = container.scrollLeft;
        const maxScroll = container.scrollWidth - container.clientWidth;
        
        setShowScrollIndicators({
          left: scrollLeft > 0,
          right: scrollLeft < maxScroll && maxScroll > 0
        });
      }
    };

    // Check on initial load and when data changes
    const timer = setTimeout(checkScrollIndicators, 100);
    return () => clearTimeout(timer);
  }, [filteredAppointments, visibleColumns]);

  const handleInputChange = async (campaignId, appointmentId, field, value) => {
    const appointmentKey = `${campaignId}_${appointmentId}`;
    
    // Update local state
    setUserNotes((prevNotes) => ({
      ...prevNotes,
      [appointmentKey]: {
        ...prevNotes[appointmentKey],
        [field]: value,
      },
    }));

    // Autosave to database
    try {
      const updatedNote = {
        ...userNotes[appointmentKey],
        [field]: value,
      };

      await axios.put(
        `${process.env.REACT_APP_API_BASE}/appointment-campaign/update?id=${campaignId}&appointmentId=${appointmentId}`,
        updatedNote,
        {
          headers: {
            Authorization: `Basic ${process.env.REACT_APP_TOKEN}`,
          },
        }
      );
    } catch (error) {
      console.error("Eroare la autosave:", error);
      toast.error("Eroare la salvare automată");
    }
  };

  const requestDeleteAppointments = (appointments) => {
    const validAppointments = (appointments || []).filter((appointment) =>
      appointment.campaignId && getAppointmentDocumentId(appointment)
    );
    if (!validAppointments.length) {
      toast.error("Înscrierea nu are un identificator valid.");
      return;
    }

    setDeleteDialogAppointments(validAppointments);
  };

  const confirmDeleteAppointments = async () => {
    if (!deleteDialogAppointments.length) return;

    setIsDeletingAppointments(true);
    const results = await Promise.allSettled(
      deleteDialogAppointments.map((appointment) =>
        deleteCampaignAppointment(
          appointment.campaignId,
          getAppointmentDocumentId(appointment)
        )
      )
    );
    const deletedAppointments = deleteDialogAppointments.filter((appointment, index) =>
      results[index].status === "fulfilled"
    );
    const deletedRowKeys = new Set(deletedAppointments.map(getAppointmentRowKey));

    if (deletedRowKeys.size) {
      setAllAppointments((current) => current.filter((appointment) =>
        !deletedRowKeys.has(getAppointmentRowKey(appointment))
      ));
      setUserNotes((current) => {
        const next = { ...current };
        deletedAppointments.forEach((appointment) => {
          delete next[`${appointment.campaignId}_${appointment.id}`];
        });
        return next;
      });
      setUserMultipleOils((current) => {
        const next = { ...current };
        deletedAppointments.forEach((appointment) => {
          delete next[`${appointment.campaignId}_${appointment.id}`];
        });
        return next;
      });
      setSelectedAppointmentKeys((current) => {
        const next = new Set(current);
        deletedRowKeys.forEach((appointmentKey) => next.delete(appointmentKey));
        return next;
      });
    }

    const failedCount = results.length - deletedRowKeys.size;
    if (deletedRowKeys.size) {
      toast.success(deletedRowKeys.size === 1
        ? "Înscrierea la mostre a fost ștearsă."
        : `${deletedRowKeys.size} înscrieri la mostre au fost șterse.`
      );
    }
    if (failedCount) {
      toast.error(failedCount === 1
        ? "O înscriere nu a putut fi ștearsă."
        : `${failedCount} înscrieri nu au putut fi șterse.`
      );
    }

    setIsDeletingAppointments(false);
    setDeleteDialogAppointments([]);
  };

  const handleInputChangeChallanges = (campaignId, appointmentId, field, value) => {
    const appointmentKey = `${campaignId}_${appointmentId}`;
    
    setUserNotes((prevNotes) => ({
      ...prevNotes,
      [appointmentKey]: {
        ...prevNotes[appointmentKey],
        [field]: value,
      },
    }));
  };

  const handleAutosaveChallanges = async (campaignId, appointmentId, field, value) => {
    const appointmentKey = `${campaignId}_${appointmentId}`;
    
    try {
      const updatedNote = {
        ...userNotes[appointmentKey],
        [field]: value,
      };

      await axios.put(
        `${process.env.REACT_APP_API_BASE}/appointment-campaign/update?id=${campaignId}&appointmentId=${appointmentId}`,
        updatedNote,
        {
          headers: {
            Authorization: `Basic ${process.env.REACT_APP_TOKEN}`,
          },
        }
      );
    } catch (error) {
      console.error("Eroare la autosave:", error);
      toast.error("Eroare la salvare automată");
    }
  };

  const addOilDropdown = (campaignId, appointmentId) => {
    const appointmentKey = `${campaignId}_${appointmentId}`;
    
    setUserMultipleOils((prev) => ({
      ...prev,
      [appointmentKey]: [
        ...(prev[appointmentKey] || []),
        ""
      ]
    }));
  };

  const handleMultipleOilsChange = (campaignId, appointmentId, index, value) => {
    const appointmentKey = `${campaignId}_${appointmentId}`;
    
    setUserMultipleOils((prev) => {
      const currentOils = prev[appointmentKey] || [];
      const updatedOils = [...currentOils];
      updatedOils[index] = value;

      return {
        ...prev,
        [appointmentKey]: updatedOils
      };
    });

    // Autosave for multiple oils
    const updatedOils = [...(userMultipleOils[appointmentKey] || [])];
    updatedOils[index] = value;

    handleInputChange(campaignId, appointmentId, "multipleOils", updatedOils.join(", "));
  };

  const handleExport = async () => {
    setShowExportModal(true);
  };

  const closeExportModal = () => {
    setShowExportModal(false);
  };

  const handleConfig = () => {
    setShowConfigModal(true);
  };

  const closeConfigModal = () => {
    setShowConfigModal(false);
  };

  // Dashboard handlers
  const handleDashboard = () => {
    setShowDashboard(true);
  };

  const closeDashboard = () => {
    setShowDashboard(false);
  };

  // Reminder handlers
  const handleEnrolledChange = async (campaignId, appointmentId, checked) => {
    const appointmentKey = `${campaignId}_${appointmentId}`;
    const appointment = allAppointments.find(apt => 
      apt.campaignId === campaignId && apt.id === appointmentId
    );

    // Update local state
    setUserNotes(prev => ({
      ...prev,
      [appointmentKey]: {
        ...prev[appointmentKey],
        inscris: checked
      }
    }));

    // If enrolled is checked, trigger notification system
    if (checked && appointment) {
      try {
        // Schedule reminder using the integrated service (handles both frontend and backend)
        await reminderSchedulerService.scheduleReminder(
          campaignId,
          appointmentId,
          appointment.name,
          appointment.lastname,
          appointment.phone
        );
        
        // toast.success(`Reminder activat pentru ${appointment.name} ${appointment.lastname}`);
      } catch (error) {
        console.error('Error scheduling reminder:', error);
        toast.error('Eroare la activarea reminder-ului');
      }
    } else if (!checked) {
      try {
        // Cancel reminder using the integrated service (handles both frontend and backend)
        await reminderSchedulerService.cancelReminder(campaignId, appointmentId);
        
        // toast.info(`Reminder dezactivat pentru ${appointment?.name} ${appointment?.lastname}`);
      } catch (error) {
        console.error('Error cancelling reminder:', error);
        toast.warning('Reminder dezactivat local, dar a apărut o eroare la backend');
      }
    }

    // Save to backend
    handleInputChange(campaignId, appointmentId, "inscris", checked);
  };

  const handleColumnVisibilityChange = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const handleTableSettingsChange = (setting, value) => {
    setTableSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const resetToDefaults = () => {
    setVisibleColumns({
      campaign: true,
      name: true,
      lastname: true,
      phone: true,
      city: false,
      triedOils: false,
      doterraAccount: false,
      challenges: true,
      samples: true,
      enrolled: true
    });
    setTableSettings({
      rowsPerPage: 50,
      autoSave: true,
      compactView: false
    });
  };

  const setBasicView = () => {
    setVisibleColumns({
      campaign: true,
      name: true,
      lastname: true,
      phone: true,
      city: false,
      triedOils: false,
      doterraAccount: false,
      challenges: true,
      samples: true,
      enrolled: true
    });
  };

  const setDetailedView = () => {
    setVisibleColumns({
      campaign: true,
      name: true,
      lastname: true,
      phone: true,
      city: true,
      triedOils: true,
      doterraAccount: true,
      challenges: true,
      samples: true,
      enrolled: true
    });
  };

  const setMinimalView = () => {
    setVisibleColumns({
      campaign: false,
      name: true,
      lastname: true,
      phone: true,
      city: false,
      triedOils: false,
      doterraAccount: false,
      challenges: false,
      samples: true,
      enrolled: true
    });
  };

  // Expose functions to parent component via ref
  useImperativeHandle(ref, () => ({
    openExportModal: () => setShowExportModal(true)
  }));

  const displayedAppointments = React.useMemo(() => {
    const sortedAppointments = filteredAppointments
      .slice()
      .sort((a, b) => new Date(b.createDate) - new Date(a.createDate));

    return tableSettings.rowsPerPage === -1
      ? sortedAppointments
      : sortedAppointments.slice(0, tableSettings.rowsPerPage);
  }, [filteredAppointments, tableSettings.rowsPerPage]);
  const displayedAppointmentKeys = displayedAppointments
    .map(getAppointmentRowKey)
    .filter(Boolean);
  const displayedSelectionRows = displayedAppointmentKeys.map((appointmentKey) => ({
    docId: appointmentKey,
  }));
  const allDisplayedAppointmentsSelected = displayedAppointmentKeys.length > 0
    && displayedAppointmentKeys.every((appointmentKey) => selectedAppointmentKeys.has(appointmentKey));
  const someDisplayedAppointmentsSelected = displayedAppointmentKeys.some((appointmentKey) =>
    selectedAppointmentKeys.has(appointmentKey)
  );

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = someDisplayedAppointmentsSelected
        && !allDisplayedAppointmentsSelected;
    }
  }, [allDisplayedAppointmentsSelected, someDisplayedAppointmentsSelected]);

  const selectedAppointments = allAppointments.filter((appointment) =>
    selectedAppointmentKeys.has(getAppointmentRowKey(appointment))
  );

  if (loading) {
    return (
      <div className="mostre-loading">
        <div className="loading-spinner"></div>
        <p>Se încarcă mostrele...</p>
      </div>
    );
  }

  return (
    <div className="mostre-table-container" onClick={clearColumnSelection}>
      <ConfirmationDialog
        open={deleteDialogAppointments.length > 0}
        title={deleteDialogAppointments.length > 1
          ? `Ștergi ${deleteDialogAppointments.length} înscrieri?`
          : "Ștergi această înscriere?"
        }
        description="Această acțiune elimină definitiv înregistrarea din tabel și nu poate fi anulată."
        confirmLabel={deleteDialogAppointments.length > 1 ? "Șterge selecția" : "Șterge înscrierea"}
        isConfirming={isDeletingAppointments}
        onConfirm={confirmDeleteAppointments}
        onCancel={() => setDeleteDialogAppointments([])}
      />
      {/* Search and Filter Section */}
      <div className="mostre-controls">
        <div className="search-section">
          <div className="search-container">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Caută după nume, prenume sau telefon..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="filter-section">
          <button
            className={`filter-toggle ${filterOpen ? 'active' : ''}`}
            onClick={() => setFilterOpen(!filterOpen)}
          >
            <Filter size={16} />
            Filtre avansate
            <ChevronDown size={16} className={`chevron ${filterOpen ? 'rotated' : ''}`} />
          </button>

          {filterOpen && (
            <div className="modern-filter-panel">
              <div className="filter-panel-header">
                <div className="filter-header-left">
                  <div className="filter-icon">
                    <Filter size={20} />
                  </div>
                  <div className="filter-header-text">
                    <h3>Filtre Avansate</h3>
                    <p>Personalizează rezultatele pentru o căutare precisă</p>
                  </div>
                </div>
                <div className="filter-header-right">
                  <div className="active-filters-indicator">
                    {Object.values(filters).filter(f => f).length > 0 && (
                      <span className="active-filters-count">
                        {Object.values(filters).filter(f => f).length}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="filter-content">
                <div className="filter-section-group">
                  <div className="filter-section-header">
                    <h4>Informații de bază</h4>
                    <div className="section-divider"></div>
                  </div>
                  <div className="filter-grid">
                    <div className="modern-filter-group">
                      <label className="modern-filter-label">
                        <span className="label-text">Campanie</span>
                        <span className="label-icon">📋</span>
                      </label>
                      <div className="select-wrapper">
                        <select
                          value={filters.campanie}
                          onChange={(e) => handleFilterChange('campanie', e.target.value)}
                          className="modern-select"
                          title="Selectează campania dorită"
                        >
                          <option value="">🌟 Toate campaniile</option>
                          {campaignOptions.map(campaign => (
                            <option key={campaign} value={campaign}>
                              📋 {campaign}
                            </option>
                          ))}
                        </select>
                        <div className="select-arrow">
                          <ChevronDown size={16} />
                        </div>
                      </div>
                    </div>

                    <div className="modern-filter-group">
                      <label className="modern-filter-label">
                        <span className="label-text">Oraș</span>
                        <span className="label-icon">🏙️</span>
                      </label>
                      <div className="select-wrapper">
                        <select
                          value={filters.oras}
                          onChange={(e) => handleFilterChange('oras', e.target.value)}
                          className="modern-select"
                          title="Selectează orașul dorit"
                        >
                          <option value="">🌍 Toate orașele</option>
                          {cityOptions.map(city => (
                            <option key={city} value={city}>
                              🏙️ {city}
                            </option>
                          ))}
                        </select>
                        <div className="select-arrow">
                          <ChevronDown size={16} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="filter-section-group">
                  <div className="filter-section-header">
                    <h4>Experiență doTERRA</h4>
                    <div className="section-divider"></div>
                  </div>
                  <div className="filter-grid">
                    <div className="modern-filter-group">
                      <label className="modern-filter-label">
                        <span className="label-text">Încercat uleiuri</span>
                        <span className="label-icon">🌿</span>
                      </label>
                      <div className="radio-group">
                        <label className="radio-option">
                          <input
                            type="radio"
                            name="incercat"
                            value=""
                            checked={filters.incercat === ""}
                            onChange={(e) => handleFilterChange('incercat', e.target.value)}
                          />
                          <span className="radio-custom"></span>
                          <span className="radio-label">Toate</span>
                        </label>
                        <label className="radio-option">
                          <input
                            type="radio"
                            name="incercat"
                            value="Da"
                            checked={filters.incercat === "Da"}
                            onChange={(e) => handleFilterChange('incercat', e.target.value)}
                          />
                          <span className="radio-custom"></span>
                          <span className="radio-label">Da</span>
                        </label>
                        <label className="radio-option">
                          <input
                            type="radio"
                            name="incercat"
                            value="Nu"
                            checked={filters.incercat === "Nu"}
                            onChange={(e) => handleFilterChange('incercat', e.target.value)}
                          />
                          <span className="radio-custom"></span>
                          <span className="radio-label">Nu</span>
                        </label>
                      </div>
                    </div>

                    <div className="modern-filter-group">
                      <label className="modern-filter-label">
                        <span className="label-text">Cont doTERRA</span>
                        <span className="label-icon">👤</span>
                      </label>
                      <div className="radio-group">
                        <label className="radio-option">
                          <input
                            type="radio"
                            name="cont"
                            value=""
                            checked={filters.cont === ""}
                            onChange={(e) => handleFilterChange('cont', e.target.value)}
                          />
                          <span className="radio-custom"></span>
                          <span className="radio-label">Toate</span>
                        </label>
                        <label className="radio-option">
                          <input
                            type="radio"
                            name="cont"
                            value="Da"
                            checked={filters.cont === "Da"}
                            onChange={(e) => handleFilterChange('cont', e.target.value)}
                          />
                          <span className="radio-custom"></span>
                          <span className="radio-label">Da</span>
                        </label>
                        <label className="radio-option">
                          <input
                            type="radio"
                            name="cont"
                            value="Nu"
                            checked={filters.cont === "Nu"}
                            onChange={(e) => handleFilterChange('cont', e.target.value)}
                          />
                          <span className="radio-custom"></span>
                          <span className="radio-label">Nu</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="filter-section-group">
                  <div className="filter-section-header">
                    <h4>Căutare avansată</h4>
                    <div className="section-divider"></div>
                  </div>
                  <div className="filter-grid">
                    <div className="modern-filter-group">
                      <label className="modern-filter-label">
                        <span className="label-text">Provocări</span>
                        <span className="label-icon">💪</span>
                      </label>
                      <div className="input-wrapper">
                        <input
                          type="text"
                          placeholder="Caută după provocări specifice..."
                          value={filters.provocari}
                          onChange={(e) => handleFilterChange('provocari', e.target.value)}
                          className="modern-input"
                        />
                        <div className="input-icon">
                          <Search size={16} />
                        </div>
                      </div>
                    </div>

                    <div className="modern-filter-group">
                      <label className="modern-filter-label">
                        <span className="label-text">Mostre</span>
                        <span className="label-icon">🧪</span>
                      </label>
                      <div className="input-wrapper">
                        <input
                          type="text"
                          placeholder="Caută după tipul de mostre..."
                          value={filters.mostre}
                          onChange={(e) => handleFilterChange('mostre', e.target.value)}
                          className="modern-input"
                        />
                        <div className="input-icon">
                          <Search size={16} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="filter-section-group">
                  <div className="filter-section-header">
                    <h4>Status înrolare</h4>
                    <div className="section-divider"></div>
                  </div>
                  <div className="filter-grid single-column">
                    <div className="modern-filter-group">
                      <label className="modern-filter-label">
                        <span className="label-text">Status înscris</span>
                        <span className="label-icon">✅</span>
                      </label>
                      <div className="toggle-group">
                        <label className={`toggle-option ${filters.inscris === "" ? "active" : ""}`}>
                          <input
                            type="radio"
                            name="inscris"
                            value=""
                            checked={filters.inscris === ""}
                            onChange={(e) => handleFilterChange('inscris', e.target.value)}
                          />
                          <span className="toggle-label">Toate</span>
                        </label>
                        <label className={`toggle-option ${filters.inscris === "Da" ? "active" : ""}`}>
                          <input
                            type="radio"
                            name="inscris"
                            value="Da"
                            checked={filters.inscris === "Da"}
                            onChange={(e) => handleFilterChange('inscris', e.target.value)}
                          />
                          <span className="toggle-label">Înscriși</span>
                        </label>
                        <label className={`toggle-option ${filters.inscris === "Nu" ? "active" : ""}`}>
                          <input
                            type="radio"
                            name="inscris"
                            value="Nu"
                            checked={filters.inscris === "Nu"}
                            onChange={(e) => handleFilterChange('inscris', e.target.value)}
                          />
                          <span className="toggle-label">Neinscriși</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modern-filter-actions">
                <div className="filter-results-info">
                  <div className="results-counter">
                    <span className="results-number">{filteredAppointments.length}</span>
                    <span className="results-text">rezultate din {allAppointments.length}</span>
                  </div>
                  {Object.values(filters).filter(f => f).length > 0 && (
                    <div className="active-filters-summary">
                      <span className="active-text">Filtre active: </span>
                      <span className="active-count">{Object.values(filters).filter(f => f).length}</span>
                    </div>
                  )}
                </div>
                <div className="filter-action-buttons">
                  <button className="modern-clear-filters" onClick={clearFilters}>
                    <X size={18} />
                    <span>Resetează toate</span>
                  </button>
                  <button className="modern-apply-filters" onClick={() => setFilterOpen(false)}>
                    <Filter size={18} />
                    <span>Aplică filtrele</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="export-section">
          <button className="config-button" onClick={handleConfig}>
            <Settings size={16} />
            Configurare
          </button>
          <button className="dashboard-button" onClick={handleDashboard}>
            <BarChart3 size={16} />
            Dashboard
          </button>
          <button className="export-button" onClick={handleExport}>
            <Download size={16} />
            Exportă
          </button>
        </div>
        
        
      </div>

      <div className="mostre-selection-toolbar">
        <button
          type="button"
          className="mostre-bulk-delete-button"
          onClick={() => requestDeleteAppointments(selectedAppointments)}
          disabled={!selectedAppointments.length || isDeletingAppointments}
        >
          <Trash2 size={16} aria-hidden="true" />
          Șterge
        </button>
        {selectedAppointmentKeys.size > 0 && (
          <span aria-live="polite">
            {getAppointmentSelectionLabel(selectedAppointmentKeys.size)}
          </span>
        )}
      </div>

      {/* Table Section */}
      <div className={`mostre-table-wrapper ${tableSettings.compactView ? 'compact-view' : ''}`}>
        <div className="table-scroll-container" onScroll={handleScroll} onClick={(e) => e.stopPropagation()}>
          <table className="mostre-table">
          <thead>
            <tr>
              <th className="col-select">
                <input
                  ref={selectAllCheckboxRef}
                  type="checkbox"
                  checked={allDisplayedAppointmentsSelected}
                  onChange={() => setSelectedAppointmentKeys((current) =>
                    toggleAllAppointmentSelection(current, displayedSelectionRows)
                  )}
                  aria-label="Selectează toate înscrierile afișate"
                />
              </th>
              {visibleColumns.campaign && (
                <th 
                  className={`col-campaign ${selectedColumns.has('campaign') ? 'column-selected' : ''}`}
                  onClick={(e) => handleColumnClick('campaign', e)}
                >
                  Campanie
                </th>
              )}
              {visibleColumns.name && (
                <th 
                  className={`col-name ${selectedColumns.has('name') ? 'column-selected' : ''}`}
                  onClick={(e) => handleColumnClick('name', e)}
                >
                  Nume
                </th>
              )}
              {visibleColumns.lastname && (
                <th 
                  className={`col-lastname ${selectedColumns.has('lastname') ? 'column-selected' : ''}`}
                  onClick={(e) => handleColumnClick('lastname', e)}
                >
                  Prenume
                </th>
              )}
              {visibleColumns.phone && (
                <th 
                  className={`col-phone ${selectedColumns.has('phone') ? 'column-selected' : ''}`}
                  onClick={(e) => handleColumnClick('phone', e)}
                >
                  Telefon
                </th>
              )}
              {visibleColumns.city && (
                <th 
                  className={`col-city ${selectedColumns.has('city') ? 'column-selected' : ''}`}
                  onClick={(e) => handleColumnClick('city', e)}
                >
                  Oraș
                </th>
              )}
              {visibleColumns.triedOils && (
                <th 
                  className={`col-tried-oils ${selectedColumns.has('triedOils') ? 'column-selected' : ''}`}
                  onClick={(e) => handleColumnClick('triedOils', e)}
                >
                  Încercat
                </th>
              )}
              {visibleColumns.doterraAccount && (
                <th 
                  className={`col-doterra ${selectedColumns.has('doterraAccount') ? 'column-selected' : ''}`}
                  onClick={(e) => handleColumnClick('doterraAccount', e)}
                >
                  Cont
                </th>
              )}
              {visibleColumns.challenges && (
                <th 
                  className={`col-challenges ${selectedColumns.has('challenges') ? 'column-selected' : ''}`}
                  onClick={(e) => handleColumnClick('challenges', e)}
                >
                  Provocări
                </th>
              )}
              {visibleColumns.samples && (
                <th 
                  className={`col-samples ${selectedColumns.has('samples') ? 'column-selected' : ''}`}
                  onClick={(e) => handleColumnClick('samples', e)}
                >
                  Mostre
                </th>
              )}
              {visibleColumns.enrolled && (
                <th 
                  className={`col-enrolled ${selectedColumns.has('enrolled') ? 'column-selected' : ''}`}
                  onClick={(e) => handleColumnClick('enrolled', e)}
                >
                  Înscris
                </th>
              )}
              <th className="col-actions">Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {filteredAppointments.length === 0 ? (
              <tr>
                <td colSpan={Object.values(visibleColumns).filter(Boolean).length + 2} className="no-results">
                  Nu au fost găsite rezultate
                </td>
              </tr>
            ) : (
              displayedAppointments.map((appointment, index) => {
                  const appointmentKey = `${appointment.campaignId}_${appointment.id}`;
                  const appointmentRowKey = getAppointmentRowKey(appointment);
                  const isDeleting = isDeletingAppointments
                    && deleteDialogAppointments.some((item) =>
                      getAppointmentRowKey(item) === appointmentRowKey
                    );
                  
                  return (
                    <tr
                      key={appointmentRowKey || appointmentKey || index}
                      className={`${userNotes[appointmentKey]?.inscris ? 'enrolled-row' : ''} ${selectedAppointmentKeys.has(appointmentRowKey) ? 'is-selected' : ''}`.trim()}
                    >
                      <td className="col-select">
                        <input
                          type="checkbox"
                          checked={selectedAppointmentKeys.has(appointmentRowKey)}
                          onChange={() => setSelectedAppointmentKeys((current) =>
                            toggleAppointmentSelection(current, appointmentRowKey)
                          )}
                          aria-label={`Selectează înscrierea pentru ${appointment.name || "utilizator"} ${appointment.lastname || ""}`.trim()}
                          disabled={!appointmentRowKey || isDeletingAppointments}
                        />
                      </td>
                      {visibleColumns.campaign && (
                        <td className={`col-campaign ${selectedColumns.has('campaign') ? 'column-selected' : ''}`}>
                          {appointment.campaignName}
                        </td>
                      )}
                      {visibleColumns.name && (
                        <td className={`col-name ${selectedColumns.has('name') ? 'column-selected' : ''}`}>
                          {appointment.name}
                        </td>
                      )}
                      {visibleColumns.lastname && (
                        <td className={`col-lastname ${selectedColumns.has('lastname') ? 'column-selected' : ''}`}>
                          {appointment.lastname}
                        </td>
                      )}
                      {visibleColumns.phone && (
                        <td className={`col-phone ${selectedColumns.has('phone') ? 'column-selected' : ''}`}>
                          {appointment.phone}
                        </td>
                      )}
                      {visibleColumns.city && (
                        <td className={`col-city ${selectedColumns.has('city') ? 'column-selected' : ''}`}>
                          {appointment.city}
                        </td>
                      )}
                      {visibleColumns.triedOils && (
                        <td className={`col-tried-oils ${selectedColumns.has('triedOils') ? 'column-selected' : ''}`}>
                          {appointment.hasTriedEssentialOils}
                        </td>
                      )}
                      {visibleColumns.doterraAccount && (
                        <td className={`col-doterra ${selectedColumns.has('doterraAccount') ? 'column-selected' : ''}`}>
                          {appointment.hasDoTerraAccount}
                        </td>
                      )}
                      {visibleColumns.challenges && (
                        <td className={`col-challenges ${selectedColumns.has('challenges') ? 'column-selected' : ''}`}>
                          <textarea
                            className={`minimal-input ${userNotes[appointmentKey]?.provocari ? "expanded" : ""}`}
                            value={userNotes[appointmentKey]?.provocari || ""}
                            onFocus={(e) => e.target.classList.add("expanded")}
                            onBlur={(e) => {
                              if (!e.target.value) {
                                e.target.classList.remove("expanded");
                              }
                              if (tableSettings.autoSave) {
                                handleAutosaveChallanges(appointment.campaignId, appointment.id, "provocari", e.target.value);
                              }
                            }}
                            onChange={(e) =>
                              handleInputChangeChallanges(appointment.campaignId, appointment.id, "provocari", e.target.value)
                            }
                          />
                        </td>
                      )}
                      {visibleColumns.samples && (
                        <td className={`col-samples ${selectedColumns.has('samples') ? 'column-selected' : ''}`}>
                          <div>
                            <DropdownWithSearchOils
                              options={oilsDatabase}
                              userOils={userNotes[appointmentKey]?.mostre || ""}
                              setUserOils={(selectedOption) => {
                                handleInputChange(appointment.campaignId, appointment.id, "mostre", selectedOption);
                              }}
                              placeholder="Selectează uleiul"
                            />

                            {(userMultipleOils[appointmentKey] || []).map((oil, oilIndex) => (
                              <div key={oilIndex} style={{ marginTop: "5px" }}>
                                <DropdownWithSearchOils
                                  options={oilsDatabase}
                                  userOils={oil}
                                  setUserOils={(selectedOption) => {
                                    handleMultipleOilsChange(appointment.campaignId, appointment.id, oilIndex, selectedOption);
                                  }}
                                  placeholder={`Ulei ${oilIndex + 2}`}
                                />
                              </div>
                            ))}

                            <button
                              className="add-oil-button"
                              onClick={() => addOilDropdown(appointment.campaignId, appointment.id)}
                              style={{
                                marginTop: "5px",
                                padding: "4px 8px",
                                fontSize: "10px",
                                backgroundColor: "#904d71",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer"
                              }}
                            >
                              Adaugă
                            </button>
                          </div>
                        </td>
                      )}
                      {visibleColumns.enrolled && (
                        <td className={`col-enrolled ${selectedColumns.has('enrolled') ? 'column-selected' : ''}`}>
                          <div className="checkbox-container">
                            <label className="custom-checkbox">
                              <input
                                type="checkbox"
                                checked={userNotes[appointmentKey]?.inscris || false}
                                onChange={(e) => {
                                  handleEnrolledChange(appointment.campaignId, appointment.id, e.target.checked);
                                }}
                                className="checkbox-input"
                              />
                              <span className="checkbox-checkmark"></span>
                            </label>
                          </div>
                        </td>
                      )}
                      <td className="col-actions">
                        <button
                          type="button"
                          className="mostre-delete-button"
                          onClick={() => requestDeleteAppointments([appointment])}
                          disabled={isDeletingAppointments || !appointmentRowKey}
                          aria-label={`Șterge înscrierea pentru ${appointment.name || "utilizator"} ${appointment.lastname || ""}`.trim()}
                        >
                          <Trash2 size={15} aria-hidden="true" />
                          {isDeleting ? "Se șterge…" : "Șterge"}
                        </button>
                      </td>
                    </tr>
                  );
                })
            )}
          </tbody>
        </table>
        </div>
        
        {/* Scroll Indicators */}
        {showScrollIndicators.left && (
          <div 
            className="scroll-indicator left visible"
            onClick={() => scrollTable('left')}
          />
        )}
        {showScrollIndicators.right && (
          <div 
            className="scroll-indicator right visible"
            onClick={() => scrollTable('right')}
          />
        )}
        
        {/* Table Status Bar */}
        <div className="table-status-bar">
          <div className="status-info">
            <span>
              Afișează {Math.min(tableSettings.rowsPerPage === -1 ? filteredAppointments.length : tableSettings.rowsPerPage, filteredAppointments.length)} din {filteredAppointments.length} rezultate
              {filteredAppointments.length !== allAppointments.length && ` (filtrate din ${allAppointments.length} total)`}
            </span>
          </div>
          {tableSettings.rowsPerPage !== -1 && filteredAppointments.length > tableSettings.rowsPerPage && (
            <div className="pagination-info">
              <span className="pagination-warning">
                ⚠️ Se afișează doar primele {tableSettings.rowsPerPage} rezultate. 
                <button 
                  className="show-all-button"
                  onClick={() => handleTableSettingsChange('rowsPerPage', -1)}
                >
                  Afișează toate
                </button>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          isOpen={showExportModal}
          onClose={closeExportModal}
          appointments={allAppointments}
          userNotes={userNotes}
          filteredAppointments={filteredAppointments}
        />
      )}

      {/* Configuration Modal */}
      {showConfigModal && (
        <div className="modal-overlay" onClick={closeConfigModal}>
          <div className="config-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Configurări Tabel</h3>
              <button className="modal-close" onClick={closeConfigModal}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-content">
              {/* Column Visibility Section */}
              <div className="config-section full-width">
                <h4>Coloane Vizibile</h4>
                
                {/* Quick Presets */}
                <div className="preset-buttons">
                  <button className="preset-btn" onClick={setMinimalView}>
                    Minimal
                  </button>
                  <button className="preset-btn" onClick={setBasicView}>
                    De bază
                  </button>
                  <button className="preset-btn" onClick={setDetailedView}>
                    Detaliat
                  </button>
                </div>

                <div className="config-grid">
                  {                  Object.entries({
                    campaign: 'Campanie',
                    name: 'Nume',
                    lastname: 'Prenume',
                    phone: 'Telefon',
                    city: 'Oraș',
                    triedOils: 'Încercat uleiuri',
                    doterraAccount: 'Cont doTERRA',
                    challenges: 'Provocări',
                    samples: 'Mostre',
                    enrolled: 'Înscris'
                  }).map(([key, label]) => (
                    <label key={key} className="config-checkbox">
                      <input
                        type="checkbox"
                        checked={visibleColumns[key]}
                        onChange={() => handleColumnVisibilityChange(key)}
                      />
                      <span className="checkmark"></span>
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Table Settings Section */}
              <div className="config-section">
                <h4>Setări Tabel</h4>
                <div className="config-row">
                  <label>Rânduri per pagină:</label>
                  <select
                    value={tableSettings.rowsPerPage}
                    onChange={(e) => handleTableSettingsChange('rowsPerPage', parseInt(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={-1}>Toate</option>
                  </select>
                </div>
                
                <label className="config-checkbox" style={{display:'none'}}>
                  <input
                    type="checkbox"
                    checked={true}
                    readOnly
                  />
                  <span className="checkmark"></span>
                  Salvare automată
                </label>

                <label className="config-checkbox">
                  <input
                    type="checkbox"
                    checked={tableSettings.compactView}
                    onChange={(e) => handleTableSettingsChange('compactView', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  Vizualizare compactă
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button className="reset-button" onClick={resetToDefaults}>
                Resetează la Implicit
              </button>
              <button className="save-button" onClick={closeConfigModal}>
                Salvează Configurația
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Modal */}
      {showDashboard && (
        <MostreDashboard 
          isOpen={showDashboard}
          onClose={closeDashboard}
          allAppointments={allAppointments}
          userNotes={userNotes}
        />
      )}
    </div>
  );
});

export default MostreTable;
